/* eslint-disable lingui/no-unlocalized-strings -- caminhos HTTP e rótulos de log, não texto de UI */
import type { z } from 'zod';

import { billingApi, creditApi, requestBlob, TIMEOUTS, type ApiRequestConfig } from '@/shared/http';

import { toBatchAssignmentBody, toReleaseToPaymentBody, toUploadUpdates, type ReleaseToPaymentFormValues } from './credit-note.mappers';
import {
  checkAverbationSchema,
  commentPageSchema,
  complianceChecklistSchema,
  cosifAccountListSchema,
  creditNoteListSchema,
  creditNoteSchema,
  creditProductSchema,
  datasetListSchema,
  fileTypeListSchema,
  fundOptionPageSchema,
  liquidationSchedulePageSchema,
  personOptionPageSchema,
  personRecordSchema,
  productOptionPageSchema,
  queryListSchema,
  rejectionReasonListSchema,
  reserveTokenListSchema,
  signatureUrlListSchema,
  workflowListSchema,
  duePaymentListSchema,
  publicPayrollLoanPageSchema,
  registeredReserveTokenListSchema,
  semearStatusListSchema,
  employmentStatusPageSchema,
  legalPersonSignatoriesSchema,
  type AssignBody,
  type DuePaymentBody,
  type ReserveTokenCreate,
  type ShareBody,
  type ApproveLiquidationBody,
  type CancelBody,
  type ListCancelBody,
  type DisapproveBody,
  type ListDisapproveBody,
  type ManualUpdateBody,
  type ReprocessStep,
} from './credit-note.wire';

// Cada função repete uma chamada do legado (U02): mesmo verbo, caminho, query, corpo e timeout. A
// referência `arquivo:linha` é do legado em src/services/… ou src/contexts/…; `tenant` só com
// `withTenant` onde o legado manda (U37-3).

const BASE = '/CreditNote';

const withSignal = (signal?: AbortSignal): ApiRequestConfig => (signal ? { signal } : {});

/** U27: fio fora do contrato avisa fora de produção e segue com o valor cru. */
function parseWire<S extends z.ZodType>(schema: S, data: unknown, label: string): z.output<S> {
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn(`${label} fora do contrato`, parsed.error.issues);
  return data as z.output<S>;
}

// ─── leitura ────────────────────────────────────────────────────────────────

/** Filtros da lista (discovery/02 §2.3); arrays repetem a chave (`qs` repeat). */
export interface CreditNoteListFilters {
  searchString?: string;
  status?: string[];
  liquidationModeList?: string[];
  closureStatus?: string;
  productCategoryDisplayList?: string[];
  productIdList?: string[];
  personIdList?: string[];
  /** Ids de envolvido; vão como `relatedPersons=<id>:<tipo>` junto com `typeOfRelationship`. */
  relatedPersons?: string[];
  typeOfRelationship?: string[];
  filterByOwnerGroupList?: string[];
  filterByCorbanList?: string[];
  fundId?: string;
  minOpApr?: number;
  maxOpApr?: number;
  initialDate?: string;
  finalDate?: string;
  initialPaymentDate?: string;
  finalPaymentDate?: string;
  initialScheduleDueDate?: string;
  finalScheduleDueDate?: string;
  maxValue?: number;
  minValue?: number;
  creditNoteNo?: number;
  ownLiquidation?: boolean;
  ownCompliance?: boolean;
  insurance?: boolean;
  isDeleted?: boolean;
}

export interface CreditNoteListParams extends CreditNoteListFilters {
  page: number;
  size: number;
  orderBy?: string;
  personId?: string;
}

/** creditContext.tsx:252-262: envolvido e tipo de relação viram um só parâmetro `id:tipo`. */
export function toListQuery(params: CreditNoteListFilters | Record<string, unknown>): Record<string, unknown> {
  const query: Record<string, unknown> = { ...params };
  const { relatedPersons, typeOfRelationship } = query as CreditNoteListFilters;
  if (!relatedPersons?.length || !typeOfRelationship?.length) return query;
  const { typeOfRelationship: _merged, ...rest } = query;
  return { ...rest, relatedPersons: relatedPersons.map((id, index) => `${id}:${typeOfRelationship[index] ?? ''}`) };
}

/** `getCreditNoteList` (creditNote.ts:61-64) via `useCreditList` (creditContext.tsx:238-268): 5 min, tenant. */
export async function listCreditNotes(params: CreditNoteListParams, signal?: AbortSignal) {
  const { personId, page, size, orderBy, ...filters } = params;
  const data = await creditApi.get<unknown>(BASE, {
    ...withSignal(signal),
    params: toListQuery({ personId, page, size, orderBy, ...filters }),
    timeout: TIMEOUTS.min5,
    withTenant: true,
  });
  return parseWire(creditNoteListSchema, data, 'GET /CreditNote');
}

/**
 * `exportCreditNotesByFilters` (creditNote.ts:66-73; creditContext.tsx:989-1001): só os filtros (sem
 * página), tenant se houver, `arraybuffer`, 5 min. A tela exige um par de datas antes (§8.2).
 */
export async function exportCreditNotes(filters: CreditNoteListFilters): Promise<ArrayBuffer> {
  return creditApi.get<ArrayBuffer>(`${BASE}/Export/excel`, {
    params: toListQuery(filters),
    responseType: 'arraybuffer',
    timeout: TIMEOUTS.min5,
    withTenant: true,
  });
}

/** `getCreditNoteById` (creditNote.ts:278-281): sem tenant. */
export async function getCreditNote(id: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`${BASE}/${id}`, withSignal(signal));
  return parseWire(creditNoteSchema, data, 'GET /CreditNote/{id}');
}

/**
 * `getCreditProductById` (services/creditProduct/creditProduct.ts:125-132), lido pelo detalhe
 * (CreditNoteFormContainer.tsx:56-57) para as regras da barra. Migra para o módulo de produto quando nascer.
 */
export async function getCreditNoteProduct(productId: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/Product/${productId}`, { ...withSignal(signal), withTenant: true });
  return parseWire(creditProductSchema, data, 'GET /Product/{id}');
}

/** `getRejectionReason` (services/RejectionReason/rejectionReason.ts:3-8). */
export async function listRejectionReasons(signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/RejectionReason', withSignal(signal));
  return parseWire(rejectionReasonListSchema, data, 'GET /RejectionReason');
}

/** `getCheckListVerifyPersonBy` (services/creditNote/complianceChecklist/complianceChecklist.ts:7-12). */
export async function listPersonComplianceRestrictions(personId: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/complianceChecklist/verifyPerson/${personId}`, withSignal(signal));
  return parseWire(complianceChecklistSchema, data, 'GET /complianceChecklist/verifyPerson/{personId}');
}

/** `getCreditNoteWorkflows` (creditNote.ts:639-643), drawer "Esteiras". */
export async function listCreditNoteWorkflows(id: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`${BASE}/${id}/workflows`, withSignal(signal));
  return parseWire(workflowListSchema, data, 'GET /CreditNote/{id}/workflows');
}

/** Tamanho fixo do drawer de comentários (CommentCreditNoteDrawer.tsx:53). */
export const COMMENTS_PAGE_SIZE = 5;

/** `getCommentCreditNote` (creditNote.ts:818-828) via `useGetCommentCreditNote` (creditContext.tsx:915-944). */
export async function listCreditNoteComments(id: string, page: number, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`${BASE}/${id}/Comments`, {
    ...withSignal(signal),
    params: { size: COMMENTS_PAGE_SIZE, page },
    withTenant: true,
  });
  return parseWire(commentPageSchema, data, 'GET /CreditNote/{id}/Comments');
}

/** `getSignaturesCreditNote` (creditNote.ts:537-540), aba Assinaturas. */
export async function listSignatureUrls(id: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`${BASE}/${id}/SignUrl`, withSignal(signal));
  return parseWire(signatureUrlListSchema, data, 'GET /CreditNote/{id}/SignUrl');
}

/** `getCosifAccountTransaction` (creditNote.ts:515-521): a query vem literal na URL. */
export async function listCosifTransferAccounts(signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/CosifAccount?transactionType=Transfer', withSignal(signal));
  return parseWire(cosifAccountListSchema, data, 'GET /CosifAccount');
}

/** `getLiquidationScheduleCreditNote` (services/LiquidationSchedule/LiquidationSchedule.ts:41-53), `page=0&size=100`. */
export async function listLiquidationSchedule(id: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`${BASE}/${id}/LiquidationScheduleCreditNote`, {
    ...withSignal(signal),
    params: { page: 0, size: 100 },
  });
  return parseWire(liquidationSchedulePageSchema, data, 'GET /CreditNote/{id}/LiquidationScheduleCreditNote');
}

/** `getDatasetAvailableReserveToken` (services/datasets/datasets.ts:38-43), "Visualizar tokens". */
export async function listAvailableReserveTokens(personId: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/NaturalPerson/${personId}/dataset/AvailableReserveToken`, withSignal(signal));
  return parseWire(reserveTokenListSchema, data, 'GET /NaturalPerson/{id}/dataset/AvailableReserveToken');
}

/**
 * `getRecoverSimulationCreditNote` (creditNote.ts:482-486), "Recuperar simulação". Pela Nova com `warranty=`, o legado leva o
 * JSON das garantias na query (o `simulationId` dele é `{id}?warranty=[…]`, CreditNoteForm.tsx:44-45).
 */
export async function getSimulation(simulationId: string, signal?: AbortSignal, warrantyQuery?: string) {
  return creditApi.get<Record<string, unknown>>(`/Amortization/${simulationId}`, { ...(warrantyQuery === undefined ? {} : { params: { warranty: warrantyQuery } }), ...withSignal(signal) });
}

/**
 * `downloadCreditNoteDocuments` (creditNote.ts:83-94): 1 id devolve o arquivo, vários um zip; lista
 * vazia omite o parâmetro (todos os ativos). Blob, 5 min.
 */
export async function downloadCreditNoteDocuments(id: string, uploadIds: readonly string[] = []): Promise<Blob> {
  return creditApi.get<Blob>(`${BASE}/${id}/documents/download`, {
    ...(uploadIds.length > 0 ? { params: { uploadIds } } : {}),
    responseType: 'blob',
    timeout: TIMEOUTS.min5,
  });
}

// ─── datasets (abas KYC e Crédito; colunas Compliance e Análise de crédito da lista) ─────────

/** Onde o dataset mora: a operação (KYC/Crédito) ou a pessoa envolvida (acordeões PF/PJ). */
export type DatasetRecordType = 'CreditNote' | 'NaturalPerson' | 'LegalPerson';

/** Datasets da própria operação: KYC (`Kyc/index.tsx:30-34`) e Crédito (`CreditNoteTabContainer.tsx:25`). */
export const CREDIT_NOTE_DATASETS = { compliance: 'complianceanalysis', credit: 'creditanalysis' } as const;

/** `getDatasetTimeout` (contexts/datasetContext.tsx:28-34): 90 s; 2 min para antecedente/ondemand. */
export function datasetTimeout(dataset: string): number {
  const name = dataset.toLowerCase();
  return name.includes('antecedente') || name.includes('ondemand') ? TIMEOUTS.min2 : TIMEOUTS.s90;
}

/**
 * `getDataset` (services/datasets/datasets.ts:15-26): leitura do último resultado, sem custo. `params`
 * é a query que o legado escreve no nome (`ClearSaleR6Fraud?getOnlyMostRecent=false`, FraudRecords/index.tsx:41-45).
 */
export async function getDataset(recordType: DatasetRecordType, id: string, dataset: string, signal?: AbortSignal, params?: Readonly<Record<string, string | number | boolean>>) {
  const data = await creditApi.get<unknown>(`/${recordType}/${id}/Dataset/${dataset}`, {
    ...withSignal(signal),
    ...(params ? { params } : {}),
    timeout: datasetTimeout(dataset),
  });
  return parseWire(datasetListSchema, data, 'GET /{recordType}/{id}/Dataset/{dataset}');
}

/**
 * `getDatasetOnlyTokensByPersonID` (services/datasets/datasets.ts:28-36): os tokens de averbação da aba
 * Crédito ("Consignado - Tokens de Averbação"), `dataset` minúsculo e `getOnlyMostRecent=false`.
 */
export async function listReserveTokenDataset(personId: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/NaturalPerson/${personId}/dataset/ReserveToken`, { ...withSignal(signal), params: { getOnlyMostRecent: false } });
  return parseWire(queryListSchema, data, 'GET /NaturalPerson/{id}/dataset/ReserveToken');
}

export interface DataprevMarginParams {
  readonly registrationNumber?: string | undefined;
  readonly personId?: string | undefined;
  readonly creditProductId?: string | undefined;
}

const definedParams = (params: DataprevMarginParams) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));

/**
 * `getDataprevMarginQueryAsync` (services/DataprevEmployee/DataprevEmployee.ts:39-43): a última consulta de
 * margem do Crédito do Trabalhador guardada (leitura, sem custo), na ordem `registrationNumber`, `personId`,
 * `creditProductId` do `DataPrevMarginQuery` (index.tsx:44-52).
 */
export async function getDataprevMarginQuery(params: DataprevMarginParams, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/DataprevEmployee/FreeMarginQuery', { ...withSignal(signal), params: definedParams(params) });
  return parseWire(queryListSchema, data, 'GET /DataprevEmployee/FreeMarginQuery');
}

/**
 * `postUpdateDataprevMarginQueryAsync` (DataprevEmployee.ts:26-37): **consulta nova à Dataprev** ("Consultar
 * margem"/"Consultar novamente"). O `DataPrevMarginQuery` manda só `registrationNumber` e `personId`.
 */
export async function requestDataprevMarginQuery(params: DataprevMarginParams) {
  return creditApi.post('/DataprevEmployee/FreeMarginQuery', {}, { params: definedParams(params) });
}

/**
 * `postDataset` (services/datasets/datasets.ts:5-13): **consulta paga ao bureau** ("Atualizar" /
 * "Obter dados", `CreditNote:RequestDataSet`). É mutação `write` com prévia (U24), nunca leitura.
 */
export async function requestDataset(recordType: DatasetRecordType, id: string, dataset: string) {
  return creditApi.post(`/${recordType}/${id}/Dataset/${dataset}`, {}, { timeout: datasetTimeout(dataset) });
}

// ─── aba Documentos ─────────────────────────────────────────────────────────

/** `useEnumContext({ size: 100, enumName: 'FileType' })` (UploadWithRelatedContainer.tsx:34-38). */
export async function listFileTypes(signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/Enum/FileType', { ...withSignal(signal), params: { size: 100 } });
  return parseWire(fileTypeListSchema, data, 'GET /Enum/FileType');
}

/** `getNaturalPersonById`/`getLegalPersonById`: os documentos de cada pessoa da operação. */
export async function getPersonRecord(recordType: 'NaturalPerson' | 'LegalPerson', id: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/${recordType}/${id}`, withSignal(signal));
  return parseWire(personRecordSchema, data, `GET /${recordType}/{id}`);
}

// ─── ações do detalhe e da lista ────────────────────────────────────────────

/**
 * `submitApprovalCreditNoteById` (creditNote.ts:288-299). `updateStartDate: true` é o "Manter proposta
 * atual"; depois do "Atualizar proposta" vai sem o parâmetro (creditNoteActions.tsx:751-773).
 */
export async function submitApproval(id: string, updateStartDate?: boolean) {
  return creditApi.post(`${BASE}/${id}/submitapproval`, {}, { params: { updateStartDate } });
}

/**
 * `putCreateCreditNoteById` (creditNote.ts:265-276): valores do formulário **sem** `toDataModel`, com
 * tenant. Usado pelo fluxo de data de início no passado; o "Salvar" do formulário é outro mapeamento.
 */
export async function updateCreditNoteFormValues(id: string, values: Record<string, unknown>) {
  return creditApi.put(`${BASE}/${id}`, values, { withTenant: true });
}

/** Status que têm `approve{Etapa}` (a lista aprova só os três primeiros, com corpo `{}`). */
export type ApprovableStatus = 'ComplianceApproval' | 'CreditApproval' | 'InstrumentApproval' | 'ManualLiquidation';

/** `approveCreditNoteById` (creditNote.ts:438-452): `ManualLiquidation` vira `Liquidation`; tira "Approval". */
export function approveStepPath(id: string, status: ApprovableStatus): string {
  const step = status === 'ManualLiquidation' ? 'Liquidation' : status;
  return `${BASE}/${id}/approve${step.replace('Approval', '')}`;
}

/**
 * Aprovar compliance/crédito/instrumento/liquidação. Detalhe manda `{ message }` (ApproveForm.tsx:26-29),
 * lista `{}` (creditNoteListActions.tsx:121), liquidação `{ CosifAccountCode, IsAutomaticBankTransfer,
 * creditAccountId }` (ApproveLiquidationContainer.tsx:49-53).
 */
export async function approveStep(
  id: string,
  status: ApprovableStatus,
  body: { message?: string | null } | ApproveLiquidationBody | Record<string, never> = {},
) {
  return creditApi.post(approveStepPath(id, status), body);
}

/** `approveliquidCreditNoteById` (creditNote.ts:500-503): "Aprovar liquidação" da lista, corpo `{}`. */
export async function approveLiquidationFromList(id: string) {
  return creditApi.post(`${BASE}/${id}/approveLiquidation`, {});
}

/** `disapproveCreditNoteById` (creditNote.ts:328-335): rejeitar compliance/crédito/instrumento/liquidação/vistoria. */
export async function disapproveCreditNote(id: string, body: DisapproveBody | ListDisapproveBody) {
  return creditApi.post(`${BASE}/${id}/disapprove`, body);
}

/** `cancelCreditNoteById` (creditNote.ts:418-421): corpo do detalhe (`attributeTypes`) ou do lote (`complianceChecklist`). */
export async function cancelCreditNote(id: string, body: CancelBody | ListCancelBody) {
  return creditApi.post(`${BASE}/${id}/cancel`, body);
}

/** Mensagem vazia sai do corpo (creditNoteActions.tsx:319-321). */
const messageBody = (message?: string | null) => (message ? { message } : {});

/** `signatureCreditNoteById` com `type: 'signature'` (creditNote.ts:345-352): "Concluir assinatura". */
export async function completeSignature(id: string, message?: string | null) {
  return creditApi.post(`${BASE}/${id}/signature`, messageBody(message));
}

/**
 * `signatureCreditNoteById` com `type: 'approveSignature'` (creditNote.ts:345-352). `?reprove=true` vai
 * literal na URL. A reprovação de validação manda a mensagem montada por `formatRejectionMessage`.
 */
export async function reviewSignature(id: string, { reprove = false, message }: { reprove?: boolean; message?: string | null } = {}) {
  return creditApi.post(`${BASE}/${id}/approveSignature${reprove ? '?reprove=true' : ''}`, messageBody(message));
}

/** `partnerSignatureCreditNoteById` (creditNote.ts:354-361): aprovar/rejeitar assinatura (Parceiro). */
export async function reviewPartnerSignature(id: string, { reprove = false, message }: { reprove?: boolean; message?: string | null } = {}) {
  return creditApi.post(`${BASE}/${id}/approveSignatureByPartner${reprove ? '?reprove=true' : ''}`, messageBody(message));
}

/** `sendSignatoryCreditNoteById` (creditNote.ts:363-367): "Enviar link assinaturas". */
export async function sendSignatureLink(id: string) {
  return creditApi.post(`${BASE}/${id}/sendNotification`, {});
}

/** `sendAssignmenteEmailCreditNoteById` (creditNote.ts:369-377): e-mail concatenado sem encode (§11.10). */
export async function sendDocumentsByEmail(id: string, email: string) {
  return creditApi.post(`${BASE}/${id}/sendassignmentemail?sendOnlyTo=${email}`, {});
}

/** `updateFiles` (creditNote.ts:379-383): "Salvar documentos". */
export async function saveDocuments(id: string, uploads: readonly Record<string, unknown>[]) {
  return creditApi.put(`${BASE}/${id}/upload`, { uploads: toUploadUpdates(uploads) });
}

/** `deleteUploadById` (creditNote.ts:385-388). */
export async function deleteDocument(id: string, uploadId: string) {
  return creditApi.delete(`${BASE}/${id}/upload/${uploadId}`);
}

/** `deleteCreditNoteById` (creditNote.ts:283-286): sem tenant (o legado passa string e o `qs` descarta). */
export async function deleteCreditNote(id: string) {
  return creditApi.delete(`${BASE}/${id}`);
}

/** `restoreCreditFromDeleted` (creditNote.ts:495-498). */
export async function restoreCreditNote(id: string) {
  return creditApi.post(`${BASE}/${id}/restore`, {});
}

/** `postRemoveBlock` (creditNote.ts:523-526): "Desbloquear operação". */
export async function removeBlock(id: string) {
  return creditApi.post(`${BASE}/${id}/RemoveBlock`, {});
}

export interface PreviewOptions {
  useWatermark: boolean;
  createPdf: boolean;
}

/** `draftPreviewCreditNoteById` (creditNote.ts:468-480): "Gerar contrato". */
export async function generateContractPreview(id: string, options: PreviewOptions) {
  return creditApi.post(`${BASE}/${id}/draftpreview`, {}, { params: { useWatermark: options.useWatermark, createPdf: options.createPdf } });
}

/** `assignmentPreviewCreditNoteById` (creditNote.ts:454-466): "Gerar cessão". */
export async function generateAssignmentPreview(id: string, options: PreviewOptions) {
  return creditApi.post(`${BASE}/${id}/assignmentpreview`, {}, { params: { useWatermark: options.useWatermark, createPdf: options.createPdf } });
}

/** `releaseToPaymentAsync` (creditNote.ts:618-637): "Aprovar liberação de pagamento". */
export async function releaseToPayment(id: string, values: ReleaseToPaymentFormValues) {
  return creditApi.post(`${BASE}/${id}/releaseToPayment`, toReleaseToPaymentBody(values));
}

/** `paymentRevisionCreditNoteById` (creditNote.ts:390-397): contas completas ou `null`. */
export async function donePaymentRevision(id: string, body: { bankAccount: Record<string, unknown> | null; changeBankAccount: Record<string, unknown> | null }) {
  return creditApi.post(`${BASE}/${id}/donePaymentRevision`, body);
}

/** `sendToWalletManagement` (creditNote.ts:404-410): lote real, corpo = ids. */
export async function sendToWalletManagement(ids: readonly string[]) {
  return creditApi.post(`${BASE}/SendToWalletManagement`, ids);
}

/** `postReprocessByStatus` (creditNote.ts:590-601): lote real, corpo = ids. */
export async function reprocessByStatus(step: ReprocessStep, ids: readonly string[]) {
  return creditApi.post(`${BASE}/ReprocessByStatus/${step}`, ids);
}

/** `manualUpdateCreditNote` (creditNote.ts:423-436): sem query (o tenant em string é descartado, §11.9). */
export async function manualUpdate(id: string, body: ManualUpdateBody) {
  return creditApi.post(`${BASE}/${id}/manualUpdate`, body);
}

/** `sendWebCredCreditNote` (creditNote.ts:488-493): "Integrar operação com WebCred", sem query. */
export async function sendToWebCred(id: string) {
  return creditApi.post(`/WebCred/${id}`, {});
}

/** `postTransferReceiptPdf` (creditNote.ts:528-535): "Gerar comprovantes", 90 s. */
export async function generateTransferReceipts(id: string, params: { generateRecepit: boolean; generateStatement: boolean }) {
  return creditApi.post(`${BASE}/${id}/TransferReceiptPdf`, {}, { params, timeout: TIMEOUTS.s90 });
}

/** `putFinishedManualWarranty` (creditNote.ts:542-549): POST apesar do nome; lista de tokens ou `[]`. */
export async function approveWarranty(id: string, tokens: readonly Record<string, unknown>[]) {
  return creditApi.post(`${BASE}/${id}/approveWarranty`, tokens);
}

/** `postDisapproveManualWarranty` (creditNote.ts:697-707; DisapproveManualWarrantyContainer.tsx:27-33). */
export async function disapproveManualWarranty(id: string, { cancel, message }: { cancel: boolean; message: string }) {
  return creditApi.post(`${BASE}/${id}/disapproveManualWarranty`, { approve: false, cancel, message, listTokens: [] });
}

/** `postDoneWarrantyRevision` (creditNote.ts:660-673): `toDataModel` mantém as 4 chaves do modelo. */
export async function doneWarrantyRevision(id: string, values: Record<string, unknown>) {
  const body: Record<string, unknown> = {};
  for (const key of ['warrantyIds', 'reason', 'observations', 'warrantyOfficeTokens']) if (key in values) body[key] = values[key];
  return creditApi.post(`${BASE}/${id}/doneWarrantyRevision`, body);
}

/** `putUpdateRevisionWarranty` (creditNote.ts:675-683): "Incluir garantias". */
export async function updateRevisionWarranty(id: string, body: { productId: string; warranty: readonly Record<string, unknown>[] }) {
  return creditApi.put(`${BASE}/${id}/warranty`, body);
}

/** `postApproveMarginReserve` (creditNote.ts:551-562): corpo montado pelo formulário, como vem. */
export async function approveMarginReserve(id: string, body: Record<string, unknown>) {
  return creditApi.post(`${BASE}/${id}/approveMarginReserve`, body);
}

/** `postDisapproveMarginReserve` (creditNote.ts:564-575): `{ message, valueMarginReserve, includePaymentFixedCosts }`. */
export async function disapproveMarginReserve(id: string, body: { message: string; valueMarginReserve?: number | null; includePaymentFixedCosts?: boolean | null }) {
  return creditApi.post(`${BASE}/${id}/disapproveMarginReserve`, body);
}

/** `postApproveVehicleInspection` (creditNote.ts:577-588). */
export async function approveVehicleInspection(id: string, message: string) {
  return creditApi.post(`${BASE}/${id}/approveVehicleInspection`, { message });
}

/** `checkAverbationCreditNoteFGTS` (creditNote.ts:603-616): query fixa `restartWf=false&ignoreRuleDays=false`. */
export async function checkAverbation(id: string) {
  const data = await creditApi.post(`${BASE}/${id}/CheckAverbacao`, {}, { params: { restartWf: false, ignoreRuleDays: false } });
  return parseWire(checkAverbationSchema, data, 'POST /CreditNote/{id}/CheckAverbacao');
}

/** `updateStatusSiapeById` (creditNote.ts:301-305). */
export async function updateSiapeStatus(id: string) {
  return creditApi.post(`${BASE}/${id}/updateStatusSIAPE`, {});
}

/** `updateStatusZetraById` (creditNote.ts:307-311). */
export async function updateZetraStatus(id: string) {
  return creditApi.post(`${BASE}/${id}/updateStatusPurchaseContract`, {});
}

/** `linkEmployerSendWebCred` (creditNote.ts:758-761): "Associar empregador" (detalhe e lote). */
export async function linkEmployer(body: {
  includeWebCred: boolean;
  ops: readonly { employerName: string; employerRegistrationNumber: string; creditNoteNo: number }[];
}) {
  return creditApi.post(`${BASE}/LinkEmployer_SendWebCred`, body);
}

/** `importVehicleWarranty` (creditNote.ts:763-769): multipart com o campo `File`. */
export async function importVehicleWarranty(file: File) {
  const form = new FormData();
  form.append('File', file);
  return creditApi.post(`${BASE}/ImportVehicleWarranty`, form);
}

/** `checkOperationalLimit` (creditNote.ts:793-800): "Verificar Limite Operacional". */
export async function checkOperationalLimit(id: string) {
  return creditApi.post(`${BASE}/${id}/check-operational-limit`, {});
}

/** `accountOpeningReviewByCreditNoteId` (creditNote.ts:802-809). */
export async function accountOpeningReview(id: string, body: Record<string, unknown>) {
  return creditApi.post(`${BASE}/${id}/AccountOpeningReview`, body);
}

/** `postCommentCreditNote` (creditNote.ts:830-839): "Deixar comentário". */
export async function postComment(id: string, comment: string) {
  return creditApi.post(`${BASE}/${id}/Comments`, { comment });
}

/** `postLiquidationScheduleClosureUpdateStatus` (creditNote.ts:841-852): Pendente=0, Cancelado=5, Finalizado=6. */
export async function updateLiquidationClosureStatus(id: string, body: { liquidationClosureStatus: 0 | 5 | 6; reason: string }) {
  return creditApi.post(`${BASE}/${id}/LiquidationScheduleClosureUpdateStatus`, body);
}

/** `postClosureAsync` (LiquidationSchedule.ts:114-123): "Encerrar conta transitória", 3 chaves. */
export async function closeTransitoryAccount(id: string, body: { cosifAccountCode: string; closeTransitoryBankAccount: boolean; paymentDate: string }) {
  const { cosifAccountCode, closeTransitoryBankAccount, paymentDate } = body;
  return creditApi.post(`${BASE}/${id}/liquidationScheduleClosure`, { cosifAccountCode, closeTransitoryBankAccount, paymentDate });
}

/** `updateCreditNoteInsurance` (creditNote.ts:854-857): switch "Seguro cancelado". */
export async function updateInsurance(id: string, insurance: boolean) {
  return creditApi.post(`${BASE}/${id}/Insurance`, { insurance });
}

/** `updateCreditNoteFund` (creditNote.ts:859-862): trocar cessionário em operação existente. */
export async function updateFund(id: string, fundId: string) {
  return creditApi.put(`${BASE}/${id}/Fund`, { fundId });
}

/** `generateBatchAssignment` (creditNote.ts:505-508): " Iniciar cessão em lote", lote real. */
export async function startBatchAssignment(creditNoteIds: readonly string[], values?: Record<string, unknown>) {
  return creditApi.post('/BatchAssignment', toBatchAssignmentBody(creditNoteIds, values));
}

/** `deleteComplianceChecklistByIds` (complianceChecklist.ts:14-20): `ids` repetido na query. */
export async function deleteComplianceRestrictions(ids: readonly string[]) {
  return creditApi.delete('/complianceChecklist', { params: { ids } });
}

/** `postShareRecordsByResourceId` (services/share/index.ts:4-14) com `CreditNote`: tenant na query. */
export async function shareCreditNote(id: string, body: { permissionLevelType: string; principalId: string; principalType: string }) {
  return creditApi.post(`${BASE}/${id}/Share`, body, { withTenant: true });
}

// ─── opções dos filtros da lista ────────────────────────────────────────────

/** Página fixa dos autocompletes do filtro (`{ page: 0, size: 10 }` em CreditNoteFilter.tsx:48-57). */
const OPTION_PAGE = { page: 0, size: 10 } as const;
const withSearch = (searchString: string | undefined) => (searchString ? { searchString } : {});

/**
 * `getProductsList` (services/creditProduct/creditProduct.ts:15-26) via `useCreditProdutList` em modo
 * autocomplete (creditProductContext.tsx:132-141): `size=10`, tenant, `isConsiderPostingDeadline=true`.
 */
export async function listProductOptions(searchString: string | undefined, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/Product', {
    ...withSignal(signal),
    params: { ...OPTION_PAGE, isConsiderPostingDeadline: true, ...withSearch(searchString) },
    withTenant: true,
  });
  return parseWire(productOptionPageSchema, data, 'GET /Product').data;
}

/** `getPersonsList` (services/person/personService.ts:8-15) via `usePersonsList` (personContext.tsx:42). */
export async function listPersonOptions(searchString: string | undefined, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/Person', { ...withSignal(signal), params: { ...OPTION_PAGE, ...withSearch(searchString) }, withTenant: true });
  return parseWire(personOptionPageSchema, data, 'GET /Person').data;
}

/** `getFundsList` (services/fund/index.ts:11-18) via `useFundsList` (fundContext.tsx:30). */
export async function listFundOptions(searchString: string | undefined, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/Fund', { ...withSignal(signal), params: { ...OPTION_PAGE, ...withSearch(searchString) }, withTenant: true });
  return parseWire(fundOptionPageSchema, data, 'GET /Fund').data;
}

// ─── ações da barra do detalhe (trilha "barra") ─────────────────────────────

/** `assign` (services/assign/index.ts:7-27) com `recordType: 'CreditNote'`: `toDataModel` guarda `userId`, `groupName`, `tenant`. */
export async function assignCreditNote(id: string, body: AssignBody) {
  const { userId, groupName, tenant } = body;
  return creditApi.post(`${BASE}/${id}/assign`, { userId, groupName, tenant });
}

/**
 * `postShareRecordsByResourceId` (services/share/index.ts:4-14) de cada envolvido, no "Definir status" de
 * Nota Comercial (ManualUpdateWithSharedContainer.tsx:109-121): `/{discriminator}/{personId}/Share`, tenant na query.
 */
export async function sharePersonRecord(resource: 'NaturalPerson' | 'LegalPerson', personId: string, body: ShareBody) {
  return creditApi.post(`/${resource}/${personId}/Share`, body, { withTenant: true });
}

/**
 * `postDisapproveMarginReserve` (creditNote.ts:564-575) com os valores do formulário: o `string()` do yup
 * (DisapproveMarginReverse.ts:6) manda `valueMarginReserve` como texto dos centavos ("5200000").
 */
export async function disapproveMarginReserveForm(id: string, body: { message: string; valueMarginReserve: string | null; includePaymentFixedCosts: boolean }) {
  return creditApi.post(`${BASE}/${id}/disapproveMarginReserve`, body);
}

/** `downloadPdfAsync` (services/walletManagement/installment/index.ts:117-123): o carnê da BillingApi, blob. */
export async function downloadInstallmentPdf(installmentId: string, fileName: string) {
  return requestBlob(billingApi, { method: 'GET', url: `/Installment/${installmentId}/DownloadPdf` }, { fallbackFileName: fileName });
}

/** `getDatasetAvailableReserveToken` (datasets.ts:38-43) lido com o formato real (`result.Token`), para os seletores de token. */
export async function listRegisteredReserveTokens(personId: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/NaturalPerson/${personId}/dataset/AvailableReserveToken`, withSignal(signal));
  return parseWire(registeredReserveTokenListSchema, data, 'GET /NaturalPerson/{id}/dataset/AvailableReserveToken');
}

/** `postRequestNaturalPersonDatasetReserveToken` (naturalPerson.ts:162-177): "Adicionar token consignado". */
export async function requestReserveTokens(personId: string, tokens: readonly ReserveTokenCreate[]) {
  return creditApi.post(`/NaturalPerson/${personId}/dataset/ReserveToken/reserveToken`, tokens);
}

/** `postDataprevInssSemearStatusAsync` (services/DataprevInss/index.ts:42-46): "Verificar proposta", 90 s. */
export async function checkSemearProposalStatus(id: string) {
  const data = await creditApi.post(`/DataprevInss/Semear/Status/${id}`, {}, { timeout: TIMEOUTS.s90 });
  return parseWire(semearStatusListSchema, data, 'POST /DataprevInss/Semear/Status/{id}');
}

/**
 * `getGetPublicPayrollLoan` (payrollAgreement.ts:44-56): as operações do "Calendário de troco". O legado
 * passa os filtros da última lista (`filterValues.filters`); aqui vai sem query (ver o relatório da barra).
 */
export async function listPublicPayrollLoans(id: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`${BASE}/${id}/GetPublicPayrollLoan`, withSignal(signal));
  return parseWire(publicPayrollLoanPageSchema, data, 'GET /CreditNote/{id}/GetPublicPayrollLoan');
}

/** `postCalculateDuePaymentAsync` (creditNote.ts:645-658): calcula o calendário de quitação. */
export async function calculateDuePayment(id: string, body: DuePaymentBody) {
  const { creditNoteId, referenceDate, numberOfDays } = body;
  const data = await creditApi.post(`${BASE}/${id}/DuePaymentSchedule`, { creditNoteId, referenceDate, numberOfDays });
  return parseWire(duePaymentListSchema, data, 'POST /CreditNote/{id}/DuePaymentSchedule');
}

// ─── escrita nas abas do detalhe (trilha "abas") ────────────────────────────
// Garantias, envolvidos e a lista de documentos vão no Salvar do formulário (PUT /CreditNote/{id}); estas
// são as chamadas próprias da aba Documentos e dos drawers (excluir, situação SIAPE, signatários da PJ). A
// assinatura de documento (`/Signature`) é de `shared/signature`.

/** `getEmploymentStatusAsync` (services/EmploymentStatus/EmploymentStatus.ts:6-9): `page`, `size` e o texto da busca. */
export async function listEmploymentStatus(searchString: string | undefined, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/EmploymentStatus', { ...withSignal(signal), params: { page: 0, size: 10, ...(searchString ? { searchString } : {}) } });
  return parseWire(employmentStatusPageSchema, data, 'GET /EmploymentStatus');
}

/** `getLegalPersonById` (useLegalPersonData, legalPersonContext): os signatários da PJ para "Importar signatários". */
export async function getLegalPersonSignatories(id: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/LegalPerson/${id}`, withSignal(signal));
  return parseWire(legalPersonSignatoriesSchema, data, 'GET /LegalPerson/{id}');
}
